import React, { useState, useEffect, useRef } from 'react';
import { Document, Page } from 'react-pdf';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Download, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';

// Set up PDF.js worker and styles
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { pdfjs } from 'react-pdf';

// Configure worker - using CDN approach that works
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;


interface TextHighlight {
  text: string;
  pageNumber: number;
  reviewItemId?: string;
  reviewItemIndex?: number;
  judgment?: 'valid' | 'invalid' | 'excessive' | 'unclear';
  reviewItemTitle?: string;
}

interface ReactPDFViewerProps {
  documentUrl: string;
  documentName: string;
  textHighlights?: TextHighlight[];
  activePageNumber?: number;
  autoScrollToHighlight?: boolean;
  targetHighlightId?: string;
  className?: string;
  maxHeight?: string;
  onHighlightClick?: (highlight: TextHighlight) => void;
}

export default function ReactPDFViewer({
  documentUrl,
  documentName,
  textHighlights = [],
  activePageNumber = 1,
  autoScrollToHighlight = false,
  targetHighlightId,
  className = "",
  maxHeight = "600px",
  onHighlightClick,
}: ReactPDFViewerProps) {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(activePageNumber);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [fetchingBlob, setFetchingBlob] = useState(true);
  // const [pageWidth, setPageWidth] = useState<number>(0);
  // const [pageHeight, setPageHeight] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);

  console.log('ReactPDFViewer render:', { 
    documentName, 
    loading, 
    error, 
    pdfBlobUrl: !!pdfBlobUrl,
    textHighlights: textHighlights.length,
    activePageNumber,
    autoScrollToHighlight,
    pageNumber,
    numPages
  });

  // Pre-fetch PDF with authentication and create blob URL
  useEffect(() => {
    const fetchPDF = async () => {
      try {
        setFetchingBlob(true);
        setError(null);
        console.log('🔧 ReactPDFViewer: Fetching PDF with authentication:', { documentName, documentUrl });

        const response = await apiRequest('GET', documentUrl);
        const downloadData = await response.json();
        
        console.log('🔧 ReactPDFViewer: Download URL response:', { documentName, downloadData });

        if (!downloadData.downloadUrl) {
          throw new Error('No download URL received from server');
        }

        const pdfResponse = await fetch(downloadData.downloadUrl);
        
        if (!pdfResponse.ok) {
          throw new Error(`Failed to fetch PDF: ${pdfResponse.status}`);
        }

        const blob = await pdfResponse.blob();
        console.log('🔧 ReactPDFViewer: PDF blob received:', { documentName, blobSize: blob.size, blobType: blob.type });

        const blobUrl = URL.createObjectURL(blob);
        setPdfBlobUrl(blobUrl);
        setFetchingBlob(false);
        
        console.log('🎉 ReactPDFViewer: PDF blob URL created successfully:', { documentName, blobUrl });

      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        console.error('❌ ReactPDFViewer: Error fetching PDF:', { documentName, error: errorMessage });
        setError(`Failed to fetch PDF: ${errorMessage}`);
        setFetchingBlob(false);
      }
    };

    if (documentUrl && documentName) {
      fetchPDF();
    }
  }, [documentUrl, documentName]);

  // Update page number when activePageNumber changes
  useEffect(() => {
    if (activePageNumber !== pageNumber) {
      setPageNumber(activePageNumber);
    }
  }, [activePageNumber]);

  // Auto-scroll to highlight when specified
  useEffect(() => {
    if (autoScrollToHighlight && textHighlights.length > 0 && containerRef.current && !loading) {
      const timer = setTimeout(() => {
        // Scroll the container into view smoothly
        containerRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        
        // Add visual emphasis
        containerRef.current?.classList.add('ring-2', 'ring-primary', 'ring-opacity-50');
        setTimeout(() => {
          containerRef.current?.classList.remove('ring-2', 'ring-primary', 'ring-opacity-50');
        }, 2000);
      }, 500); // Wait for PDF to load
      
      return () => clearTimeout(timer);
    }
  }, [autoScrollToHighlight, textHighlights, loading, pdfBlobUrl]);

  // Navigate to specific highlight when targetHighlightId is provided
  useEffect(() => {
    if (targetHighlightId && textHighlights.length > 0 && !loading && pdfBlobUrl) {
      // Find the target highlight by reviewItemId
      const targetHighlight = textHighlights.find(h => h.reviewItemId === targetHighlightId);
      
      if (targetHighlight) {
        console.log('🎯 Navigating to target highlight:', {
          targetHighlightId,
          targetHighlight,
          currentPage: pageNumber,
          targetPage: targetHighlight.pageNumber
        });
        
        // Navigate to the correct page if needed
        if (targetHighlight.pageNumber !== pageNumber) {
          setPageNumber(targetHighlight.pageNumber);
        }
        
        // Scroll to the container and add emphasis after a short delay
        const timer = setTimeout(() => {
          containerRef.current?.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
          
          // Add stronger visual emphasis for target highlight
          containerRef.current?.classList.add('ring-4', 'ring-blue-500', 'ring-opacity-75');
          setTimeout(() => {
            containerRef.current?.classList.remove('ring-4', 'ring-blue-500', 'ring-opacity-75');
            containerRef.current?.classList.add('ring-2', 'ring-blue-400', 'ring-opacity-50');
            setTimeout(() => {
              containerRef.current?.classList.remove('ring-2', 'ring-blue-400', 'ring-opacity-50');
            }, 1500);
          }, 1000);
        }, 300); // Wait for page change if needed
        
        return () => clearTimeout(timer);
      }
    }
  }, [targetHighlightId, textHighlights, loading, pdfBlobUrl]);

  // Cleanup blob URL when component unmounts
  useEffect(() => {
    return () => {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
      }
    };
  }, [pdfBlobUrl]);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    console.log('✅ ReactPDFViewer: Document loaded successfully:', { documentName, numPages });
    setNumPages(numPages);
    setLoading(false);
    setError(null);
  }

  function onDocumentLoadError(error: Error) {
    console.error('❌ ReactPDFViewer: Document load error:', { documentName, error: error.message });
    setError(error.message);
    setLoading(false);
  }

  function onPageLoadSuccess(_page: { view: number[] }) {
    console.log('✅ ReactPDFViewer: Page loaded successfully:', { documentName, pageNumber });
    // if (_page && _page.view && _page.view.length >= 4) {
    //   // _page.view gives us [x, y, width, height] of the page
    //   setPageWidth(_page.view[2]);
    //   setPageHeight(_page.view[3]);
    // }
  }

  function onPageLoadError(error: Error) {
    console.error('❌ ReactPDFViewer: Page load error:', { documentName, pageNumber, error: error.message });
  }

  const goToPrevPage = () => setPageNumber(page => Math.max(1, page - 1));
  const goToNextPage = () => setPageNumber(page => Math.min(numPages || 1, page + 1));

  const navigateToPage = (page: number) => {
    console.log('🔄 Manually navigating to page:', { 
      documentName, 
      targetPage: page, 
      currentPage: pageNumber,
      numPages,
      isValid: page >= 1 && page <= (numPages || 1)
    });
    
    if (page >= 1 && page <= (numPages || 1)) {
      setPageNumber(page);
    }
  };

  const handleDownload = () => {
    if (pdfBlobUrl) {
      const link = document.createElement('a');
      link.href = pdfBlobUrl;
      link.download = documentName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleOpenInNewTab = () => {
    if (pdfBlobUrl) {
      window.open(pdfBlobUrl, '_blank');
    }
  };

  // Get highlight colors based on review item judgment
  // const getHighlightColors = (judgment?: 'valid' | 'invalid' | 'unclear') => {
  //   switch (judgment) {
  //     case 'valid':
  //       return {
  //         main: 'bg-green-300 bg-opacity-40 border-green-500',
  //         pulse: 'bg-green-400 bg-opacity-30 border-green-600',
  //         badge: 'bg-green-500 text-white'
  //       };
  //     case 'invalid':
  //       return {
  //         main: 'bg-red-300 bg-opacity-40 border-red-500',
  //         pulse: 'bg-red-400 bg-opacity-30 border-red-600',
  //         badge: 'bg-red-500 text-white'
  //       };
  //     case 'unclear':
  //       return {
  //         main: 'bg-yellow-300 bg-opacity-40 border-yellow-500',
  //         pulse: 'bg-yellow-400 bg-opacity-30 border-yellow-600',
  //         badge: 'bg-yellow-500 text-white'
  //       };
  //     default:
  //       // Fallback for highlights without review item info
  //       return {
  //         main: 'bg-blue-300 bg-opacity-40 border-blue-500',
  //         pulse: 'bg-blue-400 bg-opacity-30 border-blue-600',
  //         badge: 'bg-blue-500 text-white'
  //       };
  //   }
  // };

  // Get highlights for current page
  const currentPageHighlights = textHighlights.filter(h => h.pageNumber === pageNumber);
  const highlightCount = textHighlights.length;
  const allHighlightPages = Array.from(new Set(textHighlights.map(h => h.pageNumber))).sort((a, b) => a - b);

  // Debug logging for highlights
  console.log('🔍 ReactPDFViewer DEBUG:', {
    documentName,
    pageNumber,
    textHighlights,
    currentPageHighlights,
    highlightCount
  });



  // Helper function to get highlight color based on judgment
  const getHighlightColor = (judgment: string) => {
    switch (judgment) {
      case 'valid':
        return 'rgba(34, 197, 94, 0.4)'; // Green
      case 'invalid':
        return 'rgba(239, 68, 68, 0.4)'; // Red
      case 'unclear':
        return 'rgba(234, 179, 8, 0.4)'; // Yellow
      default:
        return 'rgba(59, 130, 246, 0.4)'; // Blue
    }
  };

  // Add text highlighting to text layer after PDF loads
  useEffect(() => {
    if (textHighlights.length === 0 || loading || !pdfBlobUrl) {
      return;
    }

    const currentPageTextHighlights = textHighlights.filter(h => h.pageNumber === pageNumber);
    if (currentPageTextHighlights.length === 0) {
      return;
    }

    const timer = setTimeout(() => {
      console.log('🎨 Applying text highlighting:', { 
        documentName, 
        pageNumber, 
        highlightCount: currentPageTextHighlights.length,
        highlights: currentPageTextHighlights.map(h => ({ 
          text: h.text.substring(0, 100), 
          judgment: h.judgment 
        }))
      });

      // Find the text layer and add highlighting attributes
      const textLayer = containerRef.current?.querySelector('.react-pdf__Page__textContent');
      if (!textLayer) {
        console.log('❌ Text layer not found');
        return;
      }

      const spans = Array.from(textLayer.querySelectorAll('span'));
      console.log(`🔍 Found ${spans.length} text spans on page ${pageNumber}`);

      // Get all text content from the page to help with phrase matching
      // const fullPageText = spans.map(span => span.textContent || '').join(' ').toLowerCase();

      currentPageTextHighlights.forEach((highlight, highlightIndex) => {
        const searchText = highlight.text.toLowerCase().trim();
        console.log(`🎯 Processing highlight ${highlightIndex + 1}: "${searchText.substring(0, 100)}"`);

        // STRICT PHRASE MATCHING: Only highlight if we can find a substantial portion of the target phrase
        const cleanSearchText = searchText.replace(/[^\w\s]/g, ' ').replace(/\s+/g, ' ').trim();
        const searchWords = cleanSearchText.split(' ').filter(word => word.length > 2); // Only significant words
        
        console.log(`📝 Search words: [${searchWords.join(', ')}]`);
        
        // Strategy 1: Try to find the complete phrase or large chunks of it
        const targetSpans: { span: HTMLSpanElement; relevanceScore: number }[] = [];
        
        // First, try to find spans that contain substantial chunks of the target phrase
        spans.forEach((span, spanIndex) => {
          const spanText = (span.textContent || '').toLowerCase().replace(/[^\w\s]/g, ' ').replace(/\s+/g, ' ').trim();
          
          if (spanText.length === 0) return;
          
          // Calculate relevance score based on how much of the target phrase is in this span
          let score = 0;
          let consecutiveWordMatches = 0;
          let maxConsecutiveMatches = 0;
          
          // Check for consecutive word matches (indicating phrase coherence)
          for (let i = 0; i < searchWords.length - 1; i++) {
            const word1 = searchWords[i];
            const word2 = searchWords[i + 1];
            
            if (spanText.includes(word1) && spanText.includes(word2)) {
              consecutiveWordMatches++;
              maxConsecutiveMatches = Math.max(maxConsecutiveMatches, consecutiveWordMatches);
            } else {
              consecutiveWordMatches = 0;
            }
          }
          
          // Score based on:
          // 1. Percentage of search words found in this span
          // 2. Length of consecutive word matches
          // 3. How much of the span content is relevant
          const wordMatches = searchWords.filter(word => spanText.includes(word)).length;
          const wordMatchRatio = wordMatches / searchWords.length;
          const spanRelevanceRatio = searchWords.filter(word => spanText.includes(word)).join(' ').length / spanText.length;
          
          // Only consider spans with high relevance
          if (wordMatchRatio >= 0.7 && maxConsecutiveMatches >= 2) { // Need 70% word match AND consecutive words
            score = (wordMatchRatio * 0.5) + (maxConsecutiveMatches / searchWords.length * 0.3) + (spanRelevanceRatio * 0.2);
            
            console.log(`✅ High relevance span ${spanIndex}: "${spanText}" (score: ${score.toFixed(2)}, wordMatch: ${wordMatchRatio.toFixed(2)}, consecutive: ${maxConsecutiveMatches})`);
            
            targetSpans.push({ span: span as HTMLSpanElement, relevanceScore: score });
          } else if (wordMatches > 0) {
            console.log(`❌ Low relevance span ${spanIndex}: "${spanText}" (wordMatch: ${wordMatchRatio.toFixed(2)}, consecutive: ${maxConsecutiveMatches})`);
          }
        });
        
        // Strategy 2: If no high-relevance spans found, try to find the target phrase across multiple consecutive spans
        if (targetSpans.length === 0) {
          console.log(`🔄 No high-relevance spans found, trying multi-span matching...`);
          
          for (let i = 0; i < spans.length - 2; i++) {
            const spanGroup = spans.slice(i, i + 5); // Check groups of 5 consecutive spans
            const groupText = spanGroup.map(span => span.textContent || '').join(' ').toLowerCase().replace(/[^\w\s]/g, ' ').replace(/\s+/g, ' ').trim();
            
            if (groupText.length === 0) continue;
            
            const groupWordMatches = searchWords.filter(word => groupText.includes(word)).length;
            const groupWordMatchRatio = groupWordMatches / searchWords.length;
            
            // If this group of spans contains most of our target phrase
            if (groupWordMatchRatio >= 0.8) {
              console.log(`✅ Found multi-span match: "${groupText.substring(0, 100)}" (match ratio: ${groupWordMatchRatio.toFixed(2)})`);
              
              // Add individual spans from this group that contain relevant words
              spanGroup.forEach(span => {
                const spanText = (span.textContent || '').toLowerCase();
                const spanWordMatches = searchWords.filter(word => spanText.includes(word)).length;
                
                if (spanWordMatches > 0) {
                  targetSpans.push({ span: span as HTMLSpanElement, relevanceScore: 0.6 });
                }
              });
              break; // Found a good match, don't continue
            }
          }
        }
        
        // Apply highlighting only if we found relevant spans
        if (targetSpans.length > 0) {
          const highlightColor = getHighlightColor(highlight.judgment || 'valid');
          
          console.log(`🎨 Applying highlighting to ${targetSpans.length} spans for highlight "${searchText.substring(0, 50)}"`);
          
          targetSpans.forEach(({ span }, index) => {
            span.style.backgroundColor = highlightColor;
            span.style.borderRadius = '2px';
            span.style.padding = '1px 2px';
            span.style.margin = '0 1px';
            span.style.cursor = 'pointer';
            span.setAttribute('data-highlight-id', `${documentName}-${highlightIndex}-${index}`);
            span.setAttribute('title', `${highlight.judgment || 'valid'}: ${highlight.text.substring(0, 100)}...`);
            
            // Add click handler for highlight interaction
            span.addEventListener('click', () => {
              console.log('🖱️ Clicked highlight:', { documentName, highlightIndex, judgment: highlight.judgment });
              if (onHighlightClick) {
                onHighlightClick(highlight);
              }
            });
          });
        } else {
          console.log(`❌ No suitable spans found for highlight: "${searchText.substring(0, 50)}"`);
        }
      });

      console.log('✅ Text highlighting applied successfully');
    }, 500); // Small delay to ensure text layer is ready

    return () => {
      // Cleanup: remove highlighting from all spans
      const textLayer = containerRef.current?.querySelector('.react-pdf__Page__textContent');
      if (textLayer) {
        const spans = textLayer.querySelectorAll('span[data-highlight-id]');
        spans.forEach(span => {
          const spanElement = span as HTMLSpanElement;
          spanElement.style.backgroundColor = '';
          spanElement.style.border = '';
          spanElement.style.borderRadius = '';
          spanElement.style.padding = '';
          spanElement.style.cursor = '';
          spanElement.style.margin = '';
          spanElement.removeAttribute('data-highlight-id');
          spanElement.removeAttribute('title');
        });
      }
      clearTimeout(timer);
    };
  }, [textHighlights, loading, pdfBlobUrl, pageNumber, documentName, onHighlightClick]);


  // Text matching function with fuzzy matching
  // const textMatches = (fullText: string, searchText: string): boolean => {
  //   if (!fullText || !searchText) return false;
  //   
  //   const cleanFull = fullText.toLowerCase().trim();
  //   const cleanSearch = searchText.toLowerCase().trim();
  //   
  //   // Exact match
  //   if (cleanFull.includes(cleanSearch)) return true;
  //   
  //   // Word-based fuzzy matching
  //   const searchWords = cleanSearch.split(/\s+/);
  //   const fullWords = cleanFull.split(/\s+/);
  //   
  //   // Check if all search words appear in the full text (order flexible)
  //   const foundWords = searchWords.filter(searchWord => 
  //     fullWords.some(fullWord => 
  //       fullWord.includes(searchWord) || searchWord.includes(fullWord)
  //     )
  //   );
  //   
  //   // Match if we found at least 70% of search words
  //   return foundWords.length >= Math.ceil(searchWords.length * 0.7);
  // };

  // Show fetching state while getting blob URL
  if (fetchingBlob) {
    return (
      <div className={`flex flex-col items-center justify-center p-8 border rounded-lg bg-muted/30 ${className}`}>
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
        <p className="text-sm text-muted-foreground">Fetching {documentName}...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex flex-col items-center justify-center p-8 border rounded-lg bg-red-50 ${className}`}>
        <p className="text-sm text-red-600 mb-2">Failed to load document</p>
        <p className="text-xs text-red-500">{error}</p>
        <p className="text-xs text-gray-500 mt-2">URL: {documentUrl}</p>
      </div>
    );
  }

  if (!pdfBlobUrl) {
    return (
      <div className={`flex flex-col items-center justify-center p-8 border rounded-lg bg-muted/30 ${className}`}>
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
        <p className="text-sm text-muted-foreground">Preparing {documentName}...</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`border rounded-lg bg-white transition-all duration-300 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b bg-muted/50">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">
            PDF
          </Badge>
          <span className="text-sm font-medium truncate">{documentName}</span>
          {highlightCount > 0 && (
            <Badge variant="secondary" className="text-xs bg-yellow-100 text-yellow-700">
              📍 {highlightCount} highlight{highlightCount !== 1 ? 's' : ''}
            </Badge>
          )}
          {numPages && numPages > 1 && (
            <Badge variant="outline" className="text-xs">
              Page {pageNumber} of {numPages}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={handleDownload} title="Download PDF">
            <Download className="h-3 w-3" />
          </Button>
          <Button variant="ghost" size="sm" onClick={handleOpenInNewTab} title="Open in new tab">
            <ExternalLink className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {/* Page Navigation */}
      {numPages && numPages > 1 && (
        <div className="flex items-center justify-between p-3 border-b bg-muted/30">
          <div className="flex items-center gap-2">
            <Button onClick={goToPrevPage} disabled={pageNumber <= 1} size="sm">
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <Button onClick={goToNextPage} disabled={pageNumber >= numPages} size="sm">
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
          <div className="text-sm text-muted-foreground">
            Use controls to navigate pages
          </div>
        </div>
      )}

      {/* Highlight Page Navigation */}
      {highlightCount > 0 && allHighlightPages.length > 1 && (
        <div className="px-3 py-2 border-b bg-yellow-50">
          <div className="flex items-center justify-between text-xs">
            <span className="text-yellow-700 font-medium">
              Highlighted content on pages: {allHighlightPages.join(', ')}
            </span>
            <div className="flex items-center gap-2">
              <span className="text-yellow-600">Jump to:</span>
              {allHighlightPages.map(pageNum => (
                <Button
                  key={pageNum}
                  variant={pageNum === pageNumber ? "default" : "ghost"}
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => navigateToPage(pageNum)}
                >
                  Page {pageNum}
                </Button>
              ))}
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center p-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary mr-2" />
          <span>Loading PDF...</span>
        </div>
      )}

      {/* PDF Document with React-PDF - Dynamic Height Container */}
      {pdfBlobUrl && (
        <div 
          className="overflow-auto" 
          style={{ maxHeight }}
        >
          <Document
            file={pdfBlobUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={
              <div className="flex items-center justify-center p-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary mr-2" />
                <span>Loading document...</span>
              </div>
            }
            error={
              <div className="bg-red-50 border border-red-200 rounded p-4 m-4">
                <p className="text-red-600">Failed to load PDF document</p>
              </div>
            }
          >
            {!loading && !error && (
              <div className="relative">
                <div className="border-0">
                  <Page
                    pageNumber={pageNumber}
                    onLoadSuccess={onPageLoadSuccess}
                    onLoadError={onPageLoadError}
                    loading={
                      <div className="flex items-center justify-center p-8">
                        <Loader2 className="h-6 w-6 animate-spin text-primary mr-2" />
                        <span>Loading page...</span>
                      </div>
                    }
                    error={
                      <div className="bg-red-50 border border-red-200 rounded p-4 m-4">
                        <p className="text-red-600">Failed to load page {pageNumber}</p>
                      </div>
                    }
                    renderTextLayer={true} // Enable text layer for better accessibility
                    renderAnnotationLayer={false} // Disable annotations to avoid conflicts
                  />
                </div>
              </div>
            )}
          </Document>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between p-3 border-t bg-muted/50 text-xs text-muted-foreground">
        <span>
          {highlightCount > 0 
            ? `${currentPageHighlights.length} highlight${currentPageHighlights.length !== 1 ? 's' : ''} on this page`
            : 'PDF viewer ready'
          }
        </span>
        <span>
          {numPages ? `${numPages} page${numPages !== 1 ? 's' : ''} total` : 'Loading...'}
        </span>
      </div>
    </div>
  );
} 